<template>
  <div>
    <!-- Main Configuration Card -->
    <b-row>
      <b-col cols="12">
        <b-card class="shadow-sm border-0">
          <b-card-header class="border-bottom pb-3">
            <h5 class="mb-0 d-flex align-items-center">
              <feather-icon
                icon="SettingsIcon"
                size="20"
                class="mr-2 text-primary"
              />
              Taxa de Comissão da Empresa
            </h5>
          </b-card-header>
          <b-card-body class="p-4">
            <!-- Current Commission Display -->
            <div class="text-center mb-4 p-4 bg-light rounded">
              <div class="commission-display">
                <h1 class="display-3 font-weight-bold text-primary mb-2">
                  {{ currentCommission }}%
                </h1>
                <p class="text-muted mb-0">Taxa Atual de Comissão</p>
              </div>
            </div>

            <!-- Commission Slider -->
            <div class="commission-control mb-4">
              <label class="form-label font-weight-semibold mb-3">
                Ajustar Porcentagem de Comissão:
              </label>
              
              <div class="slider-container p-3 bg-light rounded">
                <b-form-group>
                  <vue-slider
                    v-model="currentCommission"
                    :min="0"
                    :max="50"
                    :interval="0.5"
                    :dot-size="20"
                    :height="8"
                    :tooltip="'always'"
                    :process-style="{ backgroundColor: '#7367f0' }"
                    :tooltip-style="{ backgroundColor: '#7367f0', borderColor: '#7367f0' }"
                    class="commission-slider"
                  >
                    <template #tooltip="{ value }">
                      <div class="custom-tooltip">
                        {{ value }}%
                      </div>
                    </template>
                  </vue-slider>
                  
                  <div class="d-flex justify-content-between mt-2">
                    <small class="text-muted">0%</small>
                    <small class="text-muted">50%</small>
                  </div>
                </b-form-group>
              </div>

              <!-- Quick Preset Buttons -->
              <div class="preset-buttons mt-3">
                <label class="form-label font-weight-semibold mb-2">
                  Valores Pré-definidos:
                </label>
                <div class="d-flex flex-wrap gap-2">
                  <b-button
                    v-for="preset in presetValues"
                    :key="preset"
                    size="sm"
                    :variant="currentCommission === preset ? 'primary' : 'outline-primary'"
                    @click="currentCommission = preset"
                  >
                    {{ preset }}%
                  </b-button>
                </div>
              </div>
            </div>

            <!-- Impact Preview -->
            <div class="impact-preview p-3 border rounded mb-4">
              <h6 class="text-dark mb-3">
                <feather-icon icon="TrendingUpIcon" size="16" class="mr-1" />
                Simulação de Impacto
              </h6>
              <b-row>
                <b-col md="4">
                  <div class="text-center">
                    <p class="text-muted mb-1 small">Venda de R$ 100,00</p>
                    <h5 class="text-success mb-0">R$ {{ (100 * currentCommission / 100).toFixed(2) }}</h5>
                  </div>
                </b-col>
                <b-col md="4">
                  <div class="text-center">
                    <p class="text-muted mb-1 small">Venda de R$ 500,00</p>
                    <h5 class="text-success mb-0">R$ {{ (500 * currentCommission / 100).toFixed(2) }}</h5>
                  </div>
                </b-col>
                <b-col md="4">
                  <div class="text-center">
                    <p class="text-muted mb-1 small">Venda de R$ 1.000,00</p>
                    <h5 class="text-success mb-0">R$ {{ (1000 * currentCommission / 100).toFixed(2) }}</h5>
                  </div>
                </b-col>
              </b-row>
            </div>

            <!-- Action Buttons -->
            <div class="d-flex justify-content-end">
              <b-button
                variant="outline-secondary"
                class="mr-2"
                @click="resetToOriginal"
              >
                <feather-icon icon="RotateCcwIcon" size="16" class="mr-1" />
                Cancelar
              </b-button>
              <b-button
                variant="primary"
                :disabled="!hasChanges"
                @click="saveCommission"
              >
                <feather-icon icon="SaveIcon" size="16" class="mr-1" />
                Salvar Alterações
              </b-button>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- Interest Rate Configuration Card -->
    <b-row class="mt-4">
      <b-col cols="12">
        <b-card class="shadow-sm border-0">
          <b-card-header class="border-bottom pb-3">
            <h5 class="mb-0 d-flex align-items-center">
              <feather-icon
                icon="PercentIcon"
                size="20"
                class="mr-2 text-warning"
              />
              Taxa de Juros em Parcelamentos
            </h5>
          </b-card-header>
          <b-card-body class="p-4">
            <!-- Current Interest Rate Display -->
            <div class="text-center mb-4 p-4 bg-light rounded">
              <div class="commission-display">
                <h1 class="display-3 font-weight-bold text-warning mb-2">
                  {{ currentInterestRate }}%
                </h1>
                <p class="text-muted mb-0">Taxa Atual de Juros em Parcelamentos</p>
              </div>
            </div>

            <!-- Interest Rate Slider -->
            <div class="commission-control mb-4">
              <label class="form-label font-weight-semibold mb-3">
                Ajustar Porcentagem de Juros:
              </label>
              
              <div class="slider-container p-3 bg-light rounded">
                <b-form-group>
                  <vue-slider
                    v-model="currentInterestRate"
                    :min="0"
                    :max="50"
                    :interval="0.5"
                    :dot-size="20"
                    :height="8"
                    :tooltip="'always'"
                    :process-style="{ backgroundColor: '#ff9f43' }"
                    :tooltip-style="{ backgroundColor: '#ff9f43', borderColor: '#ff9f43' }"
                    class="commission-slider"
                  >
                    <template #tooltip="{ value }">
                      <div class="custom-tooltip">
                        {{ value }}%
                      </div>
                    </template>
                  </vue-slider>
                  
                  <div class="d-flex justify-content-between mt-2">
                    <small class="text-muted">0%</small>
                    <small class="text-muted">50%</small>
                  </div>
                  <!-- Precise numeric input -->
                  <b-form-group
                    label="Valor preciso (%):"
                    label-for="interest-input"
                    class="mt-3 mb-0"
                  >
                    <b-input-group append="%">
                      <b-form-input
                        id="interest-input"
                        v-model.number="currentInterestRate"
                        type="number"
                        step="0.01"
                        min="0"
                        max="50"
                        @blur="normalizeInterestRate"
                        @change="clampInterestRate"
                      />
                    </b-input-group>
                    <small class="text-muted d-block mt-1">Digite um valor entre 0 e 50 com até duas casas decimais.</small>
                  </b-form-group>
                </b-form-group>
              </div>

              <!-- Quick Preset Buttons for Interest Rate -->
              <div class="preset-buttons mt-3">
                <label class="form-label font-weight-semibold mb-2">
                  Valores Pré-definidos:
                </label>
                <div class="d-flex flex-wrap gap-2">
                  <b-button
                    v-for="preset in interestPresetValues"
                    :key="preset"
                    size="sm"
                    :variant="currentInterestRate === preset ? 'warning' : 'outline-warning'"
                    @click="currentInterestRate = preset"
                  >
                    {{ preset }}%
                  </b-button>
                </div>
              </div>
            </div>

            <!-- Interest Rate Impact Preview -->
            <div class="impact-preview-interest p-3 border rounded mb-4">
              <h6 class="text-dark mb-3">
                <feather-icon icon="TrendingUpIcon" size="16" class="mr-1" />
                Simulação de Juros por Parcela
              </h6>
              <b-row>
                <b-col md="4">
                  <div class="text-center">
                    <p class="text-muted mb-1 small">3x de R$ 100,00</p>
                    <h5 class="text-warning mb-0">{{ formatCurrency(calculateInstallmentWithInterest(100, 3)) }}/mês</h5>
                    <small class="text-muted">Total: {{ formatCurrency(calculateInstallmentWithInterest(100, 3) * 3) }}</small>
                  </div>
                </b-col>
                <b-col md="4">
                  <div class="text-center">
                    <p class="text-muted mb-1 small">6x de R$ 100,00</p>
                    <h5 class="text-warning mb-0">{{ formatCurrency(calculateInstallmentWithInterest(100, 6)) }}/mês</h5>
                    <small class="text-muted">Total: {{ formatCurrency(calculateInstallmentWithInterest(100, 6) * 6) }}</small>
                  </div>
                </b-col>
                <b-col md="4">
                  <div class="text-center">
                    <p class="text-muted mb-1 small">12x de R$ 100,00</p>
                    <h5 class="text-warning mb-0">{{ formatCurrency(calculateInstallmentWithInterest(100, 12)) }}/mês</h5>
                    <small class="text-muted">Total: {{ formatCurrency(calculateInstallmentWithInterest(100, 12) * 12) }}</small>
                  </div>
                </b-col>
              </b-row>
            </div>

            <!-- Interest Rate Action Buttons -->
            <div class="d-flex justify-content-end">
              <b-button
                variant="outline-secondary"
                class="mr-2"
                @click="resetInterestToOriginal"
              >
                <feather-icon icon="RotateCcwIcon" size="16" class="mr-1" />
                Cancelar
              </b-button>
              <b-button
                variant="warning"
                :disabled="!hasInterestChanges"
                @click="saveInterestRate"
              >
                <feather-icon icon="SaveIcon" size="16" class="mr-1" />
                Salvar Alterações
              </b-button>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- Godfather Commission Configuration Card -->
    <b-row class="mt-4">
      <b-col cols="12">
        <b-card class="shadow-sm border-0">
          <b-card-header class="border-bottom pb-3">
            <h5 class="mb-0 d-flex align-items-center">
              <feather-icon
                icon="UsersIcon"
                size="20"
                class="mr-2 text-success"
              />
              Taxa de Comissão dos Padrinhos
            </h5>
          </b-card-header>
          <b-card-body class="p-4">
            <!-- Current Godfather Commission Display -->
            <div class="text-center mb-4 p-4 bg-light rounded">
              <div class="commission-display">
                <h1 class="display-3 font-weight-bold text-success mb-2">
                  {{ currentGodfatherCommission }}%
                </h1>
                <p class="text-muted mb-0">Taxa Atual de Comissão dos Padrinhos</p>
              </div>
            </div>

            <!-- Godfather Commission Slider -->
            <div class="commission-control mb-4">
              <label class="form-label font-weight-semibold mb-3">
                Ajustar Porcentagem de Comissão dos Padrinhos:
              </label>
              
              <div class="slider-container p-3 bg-light rounded">
                <b-form-group>
                  <vue-slider
                    v-model="currentGodfatherCommission"
                    :min="0"
                    :max="50"
                    :interval="0.5"
                    :dot-size="20"
                    :height="8"
                    :tooltip="'always'"
                    :process-style="{ backgroundColor: '#28a745' }"
                    :tooltip-style="{ backgroundColor: '#28a745', borderColor: '#28a745' }"
                    class="commission-slider"
                  >
                    <template #tooltip="{ value }">
                      <div class="custom-tooltip">
                        {{ value }}%
                      </div>
                    </template>
                  </vue-slider>
                  
                  <div class="d-flex justify-content-between mt-2">
                    <small class="text-muted">0%</small>
                    <small class="text-muted">50%</small>
                  </div>
                </b-form-group>
              </div>

              <!-- Quick Preset Buttons for Godfather -->
              <div class="preset-buttons mt-3">
                <label class="form-label font-weight-semibold mb-2">
                  Valores Pré-definidos:
                </label>
                <div class="d-flex flex-wrap gap-2">
                  <b-button
                    v-for="preset in presetValues"
                    :key="preset"
                    size="sm"
                    :variant="currentGodfatherCommission === preset ? 'success' : 'outline-success'"
                    @click="currentGodfatherCommission = preset"
                  >
                    {{ preset }}%
                  </b-button>
                </div>
              </div>
            </div>

            <!-- Godfather Impact Preview -->
            <div class="impact-preview-godfather p-3 border rounded mb-4">
              <h6 class="text-dark mb-3">
                <feather-icon icon="TrendingUpIcon" size="16" class="mr-1" />
                Simulação de Impacto - Padrinhos
              </h6>
              <b-row>
                <b-col md="4">
                  <div class="text-center">
                    <p class="text-muted mb-1 small">Venda de R$ 100,00</p>
                    <h5 class="text-success mb-0">R$ {{ (100 * currentGodfatherCommission / 100).toFixed(2) }}</h5>
                  </div>
                </b-col>
                <b-col md="4">
                  <div class="text-center">
                    <p class="text-muted mb-1 small">Venda de R$ 500,00</p>
                    <h5 class="text-success mb-0">R$ {{ (500 * currentGodfatherCommission / 100).toFixed(2) }}</h5>
                  </div>
                </b-col>
                <b-col md="4">
                  <div class="text-center">
                    <p class="text-muted mb-1 small">Venda de R$ 1.000,00</p>
                    <h5 class="text-success mb-0">R$ {{ (1000 * currentGodfatherCommission / 100).toFixed(2) }}</h5>
                  </div>
                </b-col>
              </b-row>
            </div>

            <!-- Godfather Action Buttons -->
            <div class="d-flex justify-content-end">
              <b-button
                variant="outline-secondary"
                class="mr-2"
                @click="resetGodfatherToOriginal"
              >
                <feather-icon icon="RotateCcwIcon" size="16" class="mr-1" />
                Cancelar
              </b-button>
              <b-button
                variant="success"
                :disabled="!hasGodfatherChanges"
                @click="saveGodfatherCommission"
              >
                <feather-icon icon="SaveIcon" size="16" class="mr-1" />
                Salvar Alterações
              </b-button>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BCard,
  BCardBody,
  BCardHeader,
  BRow,
  BCol,
  BButton,
  BFormGroup,
  BFormInput,
  BInputGroup,
} from 'bootstrap-vue'
import VueSlider from 'vue-slider-component'
import 'vue-slider-component/theme/antd.css'
import axios from '@axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BCard,
    BCardBody,
    BCardHeader,
    BRow,
    BCol,
    BButton,
    BFormGroup,
    BFormInput,
    BInputGroup,
    VueSlider,
  },
  data() {
    return {
      currentCommission: 15,
      originalCommission: 15,
      currentGodfatherCommission: 10,
      originalGodfatherCommission: 10,
      currentInterestRate: 0,
      originalInterestRate: 0,
      presetValues: [5, 10, 15, 20, 25],
      interestPresetValues: [0, 1, 1.5, 2, 2.5, 3, 4, 5],
      loading: false,
    }
  },
  computed: {
    hasChanges() {
      return this.currentCommission !== this.originalCommission
    },
    hasGodfatherChanges() {
      return this.currentGodfatherCommission !== this.originalGodfatherCommission
    },
    hasInterestChanges() {
      return this.currentInterestRate !== this.originalInterestRate
    },
  },
  mounted() {
    this.fetchCommissionConfig()
    this.fetchGodfatherCommissionConfig()
    this.fetchInterestRateConfig()
  },
  methods: {
    async fetchCommissionConfig() {
      try {
        this.loading = true
        const { data } = await axios.get('/comissao-config/1')
        if (data) {
          this.currentCommission = parseFloat(data.percentual_comissao)
          this.originalCommission = parseFloat(data.percentual_comissao)
        }
      } catch (error) {
        console.error('Erro ao buscar configuração de comissão:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao Carregar',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: 'Não foi possível carregar a configuração de comissão.',
          },
        })
      } finally {
        this.loading = false
      }
    },
    async saveCommission() {
      try {
        this.loading = true
        // Formatar o valor para o formato esperado pela API (ex: "20,00")
        const percentualFormatado = this.currentCommission.toFixed(2).replace(',', '.')
        await axios.put('/comissao-config/1', {
          percentual_comissao: percentualFormatado,
        })
        this.originalCommission = this.currentCommission
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Sucesso',
            icon: 'CheckIcon',
            variant: 'success',
            text: 'Comissão atualizada com sucesso!',
          },
        })
      } catch (error) {
        console.error('Erro ao salvar comissão:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao Salvar',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: 'Não foi possível salvar a configuração de comissão.',
          },
        })
      } finally {
        this.loading = false
      }
    },
    async fetchGodfatherCommissionConfig() {
      try {
        this.loading = true
        const { data } = await axios.get('/comissao-config/2')
        if (data) {
          this.currentGodfatherCommission = parseFloat(data.percentual_comissao)
          this.originalGodfatherCommission = parseFloat(data.percentual_comissao)
        }
      } catch (error) {
        console.error('Erro ao buscar configuração de comissão dos padrinhos:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao Carregar',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: 'Não foi possível carregar a configuração de comissão dos padrinhos.',
          },
        })
      } finally {
        this.loading = false
      }
    },
    async saveGodfatherCommission() {
      try {
        this.loading = true
        // Formatar o valor para o formato esperado pela API (ex: "20,00")
        const percentualFormatado = this.currentGodfatherCommission.toFixed(2).replace(',', '.')
        await axios.put('/comissao-config/2', {
          percentual_comissao: percentualFormatado,
        })
        this.originalGodfatherCommission = this.currentGodfatherCommission
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Sucesso',
            icon: 'CheckIcon',
            variant: 'success',
            text: 'Comissão dos padrinhos atualizada com sucesso!',
          },
        })
      } catch (error) {
        console.error('Erro ao salvar comissão dos padrinhos:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao Salvar',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: 'Não foi possível salvar a configuração de comissão dos padrinhos.',
          },
        })
      } finally {
        this.loading = false
      }
    },
    resetToOriginal() {
      this.currentCommission = this.originalCommission
    },
    resetGodfatherToOriginal() {
      this.currentGodfatherCommission = this.originalGodfatherCommission
    },
    async fetchInterestRateConfig() {
      try {
        this.loading = true
        const { data } = await axios.get('/comissao-config/3')
        if (data) {
          this.currentInterestRate = parseFloat(data.percentual_comissao)
          this.originalInterestRate = parseFloat(data.percentual_comissao)
        }
      } catch (error) {
        console.error('Erro ao buscar configuração de taxa de juros:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao Carregar',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: 'Não foi possível carregar a configuração de taxa de juros.',
          },
        })
      } finally {
        this.loading = false
      }
    },
    async saveInterestRate() {
      try {
        this.loading = true
        const percentualFormatado = this.currentInterestRate.toFixed(2).replace(',', '.')
        await axios.put('/comissao-config/3', {
          percentual_comissao: percentualFormatado,
        })
        this.originalInterestRate = this.currentInterestRate
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Sucesso',
            icon: 'CheckIcon',
            variant: 'success',
            text: 'Taxa de juros atualizada com sucesso!',
          },
        })
      } catch (error) {
        console.error('Erro ao salvar taxa de juros:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao Salvar',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: 'Não foi possível salvar a configuração de taxa de juros.',
          },
        })
      } finally {
        this.loading = false
      }
    },
    resetInterestToOriginal() {
      this.currentInterestRate = this.originalInterestRate
    },
    clampInterestRate() {
      let v = Number(this.currentInterestRate)
      if (Number.isNaN(v)) v = 0
      if (v < 0) v = 0
      if (v > 50) v = 50
      this.currentInterestRate = Number(v.toFixed(2))
    },
    normalizeInterestRate() {
      // Normaliza entrada manual e aplica clamp/duas casas
      let v = this.currentInterestRate
      if (typeof v === 'string') {
        v = v.replace(',', '.')
        v = parseFloat(v)
      }
      if (Number.isNaN(v)) v = 0
      if (v < 0) v = 0
      if (v > 50) v = 50
      this.currentInterestRate = Number(v.toFixed(2))
    },
    calculateInstallmentWithInterest(baseValue, installments) {
      // Calcula o valor da parcela com juros compostos
      const monthlyRate = this.currentInterestRate / 100
      if (monthlyRate === 0) {
        return baseValue
      }
      // Fórmula: PMT = PV * [(1 + i)^n * i] / [(1 + i)^n - 1]
      const totalValue = baseValue * installments
      const factor = (1 + monthlyRate) ** installments
      const installmentValue = (totalValue * factor * monthlyRate) / (factor - 1)
      return installmentValue
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(value)
    },
  },
}
</script>

<style lang="scss" scoped>
.bg-gradient-primary {
  background: linear-gradient(135deg, #7367f0 0%, #9c88ff 100%);
}

.commission-display {
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background: linear-gradient(90deg, #7367f0, #9c88ff);
    border-radius: 3px;
  }
}

.commission-slider {
  margin: 20px 0;
}

.custom-tooltip {
  font-weight: 600;
  font-size: 14px;
}

.preset-buttons {
  .gap-2 > * {
    margin-right: 0.5rem;
    margin-bottom: 0.5rem;
  }
}

.impact-preview {
  background: linear-gradient(135deg, #f8f9ff 0%, #f0f2ff 100%);
  border-color: #e6e8ff !important;
}

.impact-preview-godfather {
  background: linear-gradient(135deg, #f8fff9 0%, #f0fff2 100%);
  border-color: #e6ffe8 !important;
}

.impact-preview-interest {
  background: linear-gradient(135deg, #fffaf0 0%, #fff5e6 100%);
  border-color: #ffe8cc !important;
}

.stats-item {
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  
  &:last-child {
    border-bottom: none;
  }
}

.avatar-wrapper {
  .b-avatar {
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.3);
  }
}
</style>
